<template>
   <b-modal
      id="tax-returns-modal"
      size="xl"
      title="Tax Returns"
      body-class="modal-body-custom"
      @hidden="hidden"
      no-close-on-backdrop
      no-close-on-esc
   >
      <div v-if="loading" class="tax-return-modal">
         <SpinnerOverlay />
      </div>
      <div v-if="!loading" class="tax-return-modal">
         <div class="modal-header-content">
            <p v-if="view === 'default'" class="mb-3 modal-description">
               For each of these tax periods, enter the values found in the requested forms. You
               must also provide us with a copy of the forms themselves so we can verify the values.
               You may also upload any other supporting documentation.
            </p>
            <p class="mb-3 upload-modal-description" v-if="view === 'upload'">
               Supporting gross receipt documents for {{ uploadPeriod }}
            </p>
            <p v-if="view === 'upload'" class="mb-3 modal-description">
               After you select files, click <b>Add</b> button to upload.
            </p>
         </div>
         <div v-if="view === 'default'" class="tax-return-sections-container">
            <div
               v-for="taxReturn in taxReturns"
               :key="taxReturn.id"
               class="tax-return-section mb-4"
            >
               <h5 class="mb-3">
                  {{ getFormTypeLabel(taxReturn) }} {{ taxReturn.formName }}
                  <span v-if="taxReturn.state" class="text-muted">({{ taxReturn.state }})</span>
               </h5>

               <div class="table-responsive">
                  <b-table
                     :items="getTableItems(taxReturn.id)"
                     :fields="getTableFields()"
                     table-class="tax-return-table"
                  >
                     <template #cell(lineId)="data">
                        <div class="line-id-column">
                           {{ data.value }}
                        </div>
                     </template>

                     <template #cell(lineName)="data">
                        <div class="line-name-column">
                           {{ data.value }}
                        </div>
                     </template>

                     <template
                        v-for="period in displayPeriods"
                        v-slot:[`cell(period_${period.id})`]="data"
                     >
                        <div
                           v-if="data.item.id !== 'form'"
                           :key="period.id"
                           class="period-cell-content"
                        >
                           <tcp-input-group prepend="$">
                              <b-form-input
                                 :value="
                                    formatCurrencyForDisplay(getCellValue(data.item.id, period.id))
                                 "
                                 class="tax-input"
                                 :class="{
                                    'input-warning': [null, ''].includes(
                                       getCellValue(data.item.id, period.id)
                                    ),
                                 }"
                                 placeholder="0.00"
                                 @change="
                                    (value) => updateLineValue(data.item.id, period.id, value)
                                 "
                                 autocomplete="off"
                              />
                           </tcp-input-group>
                        </div>
                        <div
                           v-else
                           :key="`${data.item.id} - ${period.id}`"
                           class="period-cell-content"
                        >
                           <b-button
                              variant="primary"
                              class="upload-btn dark-button"
                              @click="uploadForm(data.item.taxReturnId, period)"
                           >
                              Upload Form
                           </b-button>
                           <div class="files-preview-container">
                              <div
                                 v-for="file in data.item[`period_${period.id}`]"
                                 :key="file.id"
                                 class="file-preview-wrapper"
                              >
                                 <img
                                    v-if="isPdf(file)"
                                    :id="`file-tooltip-${file.id}`"
                                    src="@/assets/img/pdf-file.svg"
                                    alt="PDF"
                                 />
                                 <img
                                    v-else-if="isCsv(file)"
                                    :id="`file-tooltip-${file.id}`"
                                    src="@/assets/img/csv-file.svg"
                                    alt="PDF"
                                 />
                                 <b-icon
                                    v-else
                                    :id="`file-tooltip-${file.id}`"
                                    class="file-icon"
                                    icon="file-earmark"
                                 />
                                 <p class="delete-button" @click="deleteFile(file)">Delete</p>
                                 <b-tooltip
                                    :target="`file-tooltip-${file.id}`"
                                    triggers="hover"
                                    custom-class="file-name-tooltip"
                                 >
                                    {{ file.name }}
                                 </b-tooltip>
                              </div>
                           </div>
                        </div>
                     </template>
                  </b-table>
               </div>
            </div>
         </div>

         <div v-if="view === 'upload'" class="tax-return-upload-sections-container">
            <FileUploadComponent
               :currentFiles="currentFiles"
               limits="Limit 100MB"
               :multiple="true"
               @file-delete="removeFile"
               @files-selected="selectFiles"
            >
            </FileUploadComponent>
         </div>
      </div>
      <template #modal-footer class="modal-footer-custom">
         <b-button
            v-if="view === 'upload'"
            class="cancel-button dark-button"
            variant="primary"
            @click="onBack"
            >Back</b-button
         >
         <b-button
            v-if="view !== 'upload'"
            class="finish-button dark-button"
            variant="primary"
            @click="save"
         >
            Save
         </b-button>
         <b-button
            v-if="view === 'upload' && hasPendingFiles"
            class="finish-button dark-button"
            variant="primary"
            @click="uploadFiles()"
         >
            Upload
         </b-button>
      </template>
   </b-modal>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {formatCurrency} from '@/helpers/utils';
import FileUploadComponent from '@/components/FileUploadComponent.vue';
import SpinnerOverlay from '@/views/common/widgets/SpinnerOverlay.vue';

const VIEW_MODES = {
   DEFAULT: 'default',
   UPLOAD: 'upload',
};

export default {
   name: 'TaxReturnEditModal',
   components: {SpinnerOverlay, FileUploadComponent},
   data() {
      return {
         loading: true,
         lineValues: {},
         uploadedForms: {},
         view: VIEW_MODES.DEFAULT,
         uploadPeriod: null,
         uploadPeriodId: null,
         uploadStudyId: null,
         uploadTaxReturnId: null,
         currentFiles: [],
         filterByPeriodId: null,
      };
   },
   computed: {
      ...mapGetters('taxReturn', [
         'taxReturns',
         'taxReturnLines',
         'taxReturnFiles',
         'taxReturnData',
         'taxReturnPeriods',
      ]),
      ...mapGetters('companies', ['currentCompany', 'activeStudy']),

      displayPeriods() {
         if (this.filterByPeriodId) {
            return this.taxReturnPeriods.filter((period) => period.id === this.filterByPeriodId);
         }
         return this.taxReturnPeriods;
      },

      companyId() {
         return this.currentCompany.id;
      },

      hasPendingFiles() {
         return this.currentFiles.some((file) => !file.existing);
      },
   },

   methods: {
      ...mapActions('taxReturn', [
         'loadTaxReturns',
         'loadTaxReturnLines',
         'loadTaxReturnData',
         'fetchFiles',
         'deleteTaxReturnFile',
         'uploadTaxReturnFiles',
         'updateTaxReturnData',
      ]),

      formatCurrency,

      formatCurrencyForDisplay(value) {
         if (value === null || value === undefined || value === '') {
            return '';
         }
         return this.formatCurrency(value / 100);
      },

      sanitizeCurrencyInput(value) {
         if (value === null || value === undefined) return null;
         const str = String(value)
            .replace(/[^\d.-]/g, '')
            .trim();
         if (str === '' || str === '.' || str === '-' || str === '-.') return null;
         const num = Number(str);
         return Number.isFinite(num) ? num : null;
      },

      getCellValue(lineId, periodId) {
         const key = `${lineId}-${periodId}`;
         if (Object.prototype.hasOwnProperty.call(this.lineValues, key)) {
            return this.lineValues[key];
         }
         return this.getLineValue(lineId, periodId);
      },

      async loadData(periodId) {
         try {
            this.loading = true;
            await Promise.all([
               this.loadTaxReturns({companyId: this.companyId, periodId}),
               this.loadTaxReturnLines({companyId: this.companyId, periodId}),
               this.loadTaxReturnData({
                  companyId: this.companyId,
                  periodId,
                  studyId: this.activeStudy?.id,
               }),
               this.fetchFiles({companyId: this.companyId}),
            ]);
         } catch (error) {
            console.error('Error loading tax return data:', error);
         } finally {
            this.loading = false;
         }
      },

      getLineValue(lineId, periodId) {
         const item = this.taxReturnData.find(
            (item) => item.taxReturnLineId == lineId && item.periodId === periodId
         );

         return item?.value || null;
      },

      async updateLineValue(lineId, periodId, value) {
         const parsed = this.sanitizeCurrencyInput(value);
         const cents = parsed != null ? Math.round(parsed * 100) : null;
         this.$set(this.lineValues, `${lineId}-${periodId}`, cents);
         await this.$store.dispatch('taxReturn/updateTaxReturnItem', {
            periodId,
            lineId,
            value: cents,
         });
         this.updateTaxReturnData({lineId, periodId, value: cents});
      },

      getLinesForTaxReturn(taxReturnId) {
         return this.taxReturnLines.filter((line) => line.taxReturnId === taxReturnId);
      },

      getTableFields() {
         const fields = [
            {
               key: 'lineId',
               label: 'Line Id',
               thClass: 'period-column',
               tdClass: 'line-id-column',
            },
            {
               key: 'lineName',
               label: 'Line Name',
               thClass: 'period-column',
               tdClass: 'line-name-column',
            },
         ];

         const filteredPeriods = this.displayPeriods;

         // Add period columns
         filteredPeriods.forEach((period) => {
            fields.push({
               key: `period_${period.id}`,
               label: period.label,
               thClass: 'text-start period-header',
               tdClass: 'text-center period-cell',
            });
         });

         return fields;
      },

      getTableItems(taxReturnId) {
         const lines = this.getLinesForTaxReturn(taxReturnId);
         const result = lines.map((line) => {
            const item = {
               id: line.id,
               lineName: line.lineName,
               lineId: line.lineId,
            };

            // Add period data to each row
            this.displayPeriods.forEach((period) => {
               item[`period_${period.id}`] = this.getLineValue(line.id, period.id);
            });

            return item;
         });
         const formItem = {
            id: 'form',
            lineName: 'Form',
            lineId: '',
            taxReturnId,
         };
         this.displayPeriods.forEach((period) => {
            formItem[`period_${period.id}`] = this.getTaxFilesForPeriod(taxReturnId, period.id);
         });
         result.push(formItem);
         return result;
      },

      getFormTypeLabel(taxReturn) {
         if (taxReturn.state) {
            return '';
         }
         return 'Federal Data for';
      },

      hasUploadedForm(taxReturnId, periodId) {
         const files = this.getTaxFilesForPeriod(taxReturnId, periodId);
         return files && files.length > 0;
      },

      getFileName(taxReturnId, periodId) {
         const files = this.getTaxFilesForPeriod(taxReturnId, periodId);
         // Returns the name of the first file, or a default placeholder if not loaded properly
         return files[0]?.name || files[0]?.filename || 'Document.pdf';
      },

      isCsv(file) {
         return file.name.split('.').pop() === 'csv';
      },

      isPdf(file) {
         return file.name.split('.').pop() === 'pdf';
      },

      getTaxFilesForPeriod(taxReturnId, periodId) {
         return this.taxReturnFiles.filter((file) => {
            const filePeriodId = file.periodId;
            const fileTaxReturnId = file.validation?.data?.taxReturnId;

            return (
               String(filePeriodId) === String(periodId) &&
               String(fileTaxReturnId) === String(taxReturnId)
            );
         });
      },

      uploadForm(taxReturnId, period) {
         this.uploadPeriod = period.label;
         this.uploadPeriodId = period.id;
         this.uploadStudyId = period.studyId ?? period.study_id ?? null;
         this.uploadTaxReturnId = taxReturnId;
         this.currentFiles = this.getUploadedTaxFiles(taxReturnId, period.id);
         this.view = VIEW_MODES.UPLOAD;
      },

      getUploadedTaxFiles(taxReturnId, periodId) {
         return this.getTaxFilesForPeriod(taxReturnId, periodId).map((file) => ({
            ...file,
            existing: true,
         }));
      },

      async refreshUploadedTaxFiles() {
         await this.fetchFiles({companyId: this.companyId});

         if (this.uploadTaxReturnId == null || this.uploadPeriodId == null) {
            this.currentFiles = [];
            return;
         }

         this.currentFiles = this.getUploadedTaxFiles(this.uploadTaxReturnId, this.uploadPeriodId);
      },

      async removeFile(file) {
         if (file?.existing) {
            if (file.id == null) {
               return;
            }

            await this.deleteTaxReturnFile({fileId: file.id, force: true});
            await this.refreshUploadedTaxFiles();
            return;
         }

         this.currentFiles = this.currentFiles.filter((selectedFile) => selectedFile !== file);
      },

      async deleteFile(file) {
         await this.deleteTaxReturnFile({fileId: file.id, force: true});
      },

      selectFiles(files) {
         const selectedFiles = Array.from(files || []);
         const currentFiles = this.currentFiles || [];

         const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`;
         const existing = new Set(currentFiles.map(fileKey));
         const merged = [...currentFiles];

         selectedFiles.forEach((file) => {
            if (!existing.has(fileKey(file))) {
               existing.add(fileKey(file));
               merged.push(file);
            }
         });

         this.currentFiles = merged;
      },

      async uploadFiles() {
         const pendingFiles = this.currentFiles.filter((file) => !file.existing);
         await this.uploadTaxReturnFiles({
            files: pendingFiles,
            taxReturnId: this.uploadTaxReturnId,
            periodId: this.uploadPeriodId,
            studyId: this.uploadStudyId,
         });
         this.view = VIEW_MODES.DEFAULT;
         this.currentFiles = [];
         this.uploadPeriod = null;
         this.uploadPeriodId = null;
         this.uploadStudyId = null;
         this.uploadTaxReturnId = null;
      },

      save() {
         this.closeModal();
      },

      onBack() {
         this.view = 'default';
      },

      show(periodId) {
         this.filterByPeriodId = periodId;
         this.loadData(periodId);
         this.$bvModal.show('tax-returns-modal');
      },

      closeModal() {
         this.$bvModal.hide('tax-returns-modal');
      },

      hidden() {
         this.filterByPeriodId = null;
         this.view = VIEW_MODES.DEFAULT;
         this.$emit('afterClose');
      },
   },
};
</script>

<style scoped lang="scss">
.tax-return-modal {
   display: flex;
   flex-direction: column;
   height: 70vh;
   max-height: 70vh;
}

.modal-header-content {
   flex-shrink: 0;
   padding-bottom: 1rem;
   margin-bottom: 1rem;
   background: white;
}

.modal-description {
   font-size: 0.75rem;
   font-weight: normal;
   color: black;
   margin-bottom: 0;
}

.upload-modal-description {
   font-size: 1rem;
   font-weight: bold;
}

.tax-return-sections-container {
   flex: 1;
   overflow-y: auto;
   overflow-x: hidden;
   padding-right: 0.3125rem;
}

.tax-return-upload-sections-container {
   flex: 1;
   flex-direction: column;
   padding-right: 1rem;
}

.tax-return-sections-container::-webkit-scrollbar {
   width: 0.375rem;
}

.tax-return-sections-container::-webkit-scrollbar-track {
   background: #f1f1f1;
   border-radius: 3px;
}

.tax-return-sections-container::-webkit-scrollbar-thumb {
   background: #c1c1c1;
   border-radius: 3px;
}

.tax-return-sections-container::-webkit-scrollbar-thumb:hover {
   background: #a8a8a8;
}

::v-deep .modal-body-custom {
   padding: 1.5rem;
   height: 70vh;
   overflow: hidden;
   display: flex;
   flex-direction: column;
}

/* Ensure table containers can scroll horizontally */
.table-responsive {
   overflow-x: auto;
   -webkit-overflow-scrolling: touch;
}

::v-deep .file-preview-container {
   max-height: 18.125rem;
   overflow-y: auto;
}

::v-deep .file-preview-container::-webkit-scrollbar {
   width: 8px; /* always visible width */
}

::v-deep .file-preview-container::-webkit-scrollbar-track {
   background: #eee; /* visible track */
}

::v-deep .file-preview-container::-webkit-scrollbar-thumb {
   background: #999; /* visible thumb */
   border-radius: 6px;
}

/* Affect the rendered b-table internals */
::v-deep .tax-return-table {
   font-size: 0.75rem;
   white-space: nowrap; /* prevent wrapping so horizontal scroll engages */
}

/* Make period columns wide enough to show fixed-width inputs and button */
::v-deep .tax-return-table th.period-header,
::v-deep .tax-return-table td.period-cell {
   min-width: 9.25rem;
   vertical-align: top;
}

.files-preview-container {
   display: flex;
   flex-direction: column;
   gap: 0.25rem;
   padding: 0.5rem 0;
}

.delete-button {
   font-size: 0.75rem;
   color: $red;
   margin: 0;
   &:hover {
      cursor: pointer;
   }
}

.file-preview-wrapper {
   display: flex;
   align-items: center;
   gap: 0.5rem;
}

.file-icon {
   font-size: 1.1875rem;
}

::v-deep .file-name-tooltip {
   opacity: 1 !important;
   .tooltip-inner {
      font-size: 0.75rem !important;
      min-width: auto !important;
      color: $white !important;
   }
}

::v-deep .bs-tooltip-auto[x-placement^='top'] .arrow::before,
::v-deep .bs-tooltip-top .arrow::before {
   border-width: 6px;
   border-top-color: #ccc !important;
   top: -1px;
}

::v-deep .bs-tooltip-auto[x-placement^='top'] .arrow::after,
::v-deep .bs-tooltip-top .arrow::after {
   content: '';
   position: absolute;
   border-width: 5px;
   border-style: solid;
   border-color: transparent;
   border-top-color: white;
   top: -1px;
   left: 1px;
}

::v-deep .tcp-input-group .prepend {
   top: 2px !important;
}

/* Ensure the input group doesn't stretch so fixed input width is honored */
::v-deep .period-cell-content .tcp-input-group {
   width: auto !important;
}

.file-name {
   font-size: 0.75rem;
}

::v-deep .line-id-column {
   padding-top: 0.5em;
   vertical-align: top;
   min-width: 3rem;
}

::v-deep .line-name-column {
   padding-top: 0.5em;
   vertical-align: top;
   min-width: 22rem;
}

.period-cell-content {
   display: flex;
   flex-direction: column;
   align-items: flex-start;
   justify-content: center;
   gap: 0.25rem;
}

.upload-btn {
   font-size: 0.75rem;
   width: 8.125rem;
}

.file-indicator {
   text-align: center;
}

.file-indicator i {
   margin-right: 4px;
}

.modal-footer-custom {
   display: flex;
   justify-content: flex-end;
   gap: 0.75rem;
   padding-top: 1.25rem;
   border-top: 1px solid #dee2e6;
   margin-top: 1.25rem;
}

.tax-return-section {
   padding: 1.25rem 1.5rem 1.25rem 0;
   background-color: #fff;
}

.tax-return-section h5 {
   color: black;
   font-weight: bold;
   font-size: 1rem;
}

.tax-input {
   font-size: 0.75rem;
   width: 8.125rem;
}

.action-button {
   font-size: 0.75rem;
}

.finish-button {
   font-size: 0.875rem;
   width: 7.5rem;
}

.cancel-button {
   font-size: 0.875rem;
   width: 7.5rem;
}

@media (min-width: 959px) and (max-width: 991px) {
   ::v-deep .line-name-column {
      min-width: 13rem;
   }

   ::v-deep .modal-dialog {
      max-width: 730px;
   }
}
</style>
